/**
 * REQ-20260421010 客戶存貨紀錄 —— 前後端共用 schema。
 *
 * quantity 以 string 傳輸(避免 JS Number 精度問題),DB 側為 Decimal(14,4)。
 * recordedAt 是 date-only(YYYY-MM-DD),不含時間、不含時區。
 */
import { z } from 'zod';

/** 數量:非負,最多 4 位小數;以 string 傳輸。 */
const quantitySchema = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,4})?$/, 'quantity 須為非負數,最多 4 位小數');

const recordedAtSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'recordedAt 格式須為 YYYY-MM-DD');

export const CreateInventoryRecordSchema = z.object({
  itemName: z.string().trim().min(1).max(200),
  recordedAt: recordedAtSchema,
  quantity: quantitySchema,
  unit: z.string().trim().max(20).nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateInventoryRecordInput = z.infer<typeof CreateInventoryRecordSchema>;

export const UpdateInventoryRecordSchema = CreateInventoryRecordSchema.partial();
export type UpdateInventoryRecordInput = z.infer<typeof UpdateInventoryRecordSchema>;

/** 圖片附件:僅 JPEG / PNG / WebP;10 MB 上限。 */
export const INVENTORY_ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const INVENTORY_MAX_BYTES = 10 * 1024 * 1024;

export const PresignInventoryAttachmentSchema = z.object({
  originalName: z.string().min(1).max(255),
  mimeType: z.enum(INVENTORY_ALLOWED_MIMES),
  sizeBytes: z.number().int().positive().max(INVENTORY_MAX_BYTES),
});
export type PresignInventoryAttachmentInput = z.infer<typeof PresignInventoryAttachmentSchema>;

export const CreateInventoryAttachmentSchema = PresignInventoryAttachmentSchema.extend({
  storedName: z.string().min(1),
  storagePath: z.string().min(1),
});
export type CreateInventoryAttachmentInput = z.infer<typeof CreateInventoryAttachmentSchema>;

export const InventoryRecordAttachmentResponseSchema = z.object({
  uuid: z.string().uuid(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});
export type InventoryRecordAttachmentResponse = z.infer<
  typeof InventoryRecordAttachmentResponseSchema
>;

export const InventoryRecordCreatorBriefSchema = z.object({
  id: z.number().int().positive(),
  displayName: z.string(),
});

export const InventoryRecordResponseSchema = z.object({
  id: z.number().int().positive(),
  uuid: z.string().uuid(),
  customerId: z.number().int().positive(),
  itemName: z.string(),
  recordedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  quantity: z.string(),
  unit: z.string().nullable(),
  notes: z.string().nullable(),
  createdBy: InventoryRecordCreatorBriefSchema,
  attachment: InventoryRecordAttachmentResponseSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type InventoryRecordResponse = z.infer<typeof InventoryRecordResponseSchema>;

export const InventoryRecordListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
});
export type InventoryRecordListQuery = z.infer<typeof InventoryRecordListQuerySchema>;

export const PresignInventoryUploadResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storedName: z.string(),
  storagePath: z.string(),
  expiresInSec: z.number().int().positive(),
});
export type PresignInventoryUploadResponse = z.infer<
  typeof PresignInventoryUploadResponseSchema
>;
